import React from "react";
import { Stack, Typography } from "@mui/material";

import {
  HubButton,
  HubHookFormInput,
  HubHookFormSelect,
  HubHookFormTimePicker,
  StationHookFormSelect,
} from "../../../components";

import { StyledHubCardStack } from "../styles";
import { useHighlightableField } from "../../../hooks";
import { WaxRoutingTable } from "../../WaxRoutingTable";
import { useFieldArray } from "react-hook-form";

const gap = "10px";
const columnGap = "10px";
const injections = [
  { id: 0, label: "Hand Injection" },
  { id: 1, label: "Side Injection" },
  { id: 2, label: "Top Injection" },
  { id: 3, label: "Automatic Injection" },
];
const cupTypes = [
  { id: 1, label: "Ounces" },
  { id: 2, label: "Regular" },
  { id: 3, label: "Filter" },
];

export const PartWaxDetails = ({ basePath, routeOps }) => {
  const { HighlightMenu, getFieldProps } = useHighlightableField();
  const routingPath = `${basePath}.routing`;
  const config = `${basePath}.config`;

  const waxFieldOptions = useFieldArray({ name: routingPath });
  const { fields, append, remove, move } = waxFieldOptions;

  // When mounted by WorkOrderInstructions, routeOps mediates every
  // routing edit so the WO-global routes[] + scheduling stay in sync.
  const handleAddWax = () => {
    const newItem = {
      stationId: null,
      leadTime: 1440,
      cycleTime: 0,
      notes: "",
      config: { materialId: null },
    };

    if (routeOps?.add)
      routeOps.add(basePath, fields?.length, () => append(newItem));
    else append(newItem);
  };

  const tableMove = (start, end) => {
    if (routeOps?.move) routeOps.move(basePath, start, end, move);
    else move(start, end);
  };

  const tableRemove = (idx) => {
    if (routeOps?.remove) routeOps.remove(basePath, idx, remove);
    else remove(idx);
  };

  return (
    <StyledHubCardStack>
      <Stack gap={gap}>
        <Typography variant="boldCaptionMain">Wax Details</Typography>
        <Stack gap="25px">
          <Stack gap={gap}>
            <WaxRoutingTable
              {...waxFieldOptions}
              move={tableMove}
              remove={tableRemove}
              basePath={routingPath}
              getFieldProps={getFieldProps}
            />
            <HubButton
              label="Add Wax"
              variant="text"
              sx={{ fontSize: 10, ml: "auto", mb: -5 }}
              onClick={handleAddWax}
            />
          </Stack>
          <Stack direction="row" gap={columnGap}>
            <HubHookFormSelect
              name={`${config}.injectType`}
              label="Injection Type"
              options={injections}
              useDefault
              {...getFieldProps(`${config}.injectType`)}
            />
            <HubHookFormInput
              name={`${config}.moldNumber`}
              label="Mold #"
              placeholder="Ex. 12"
              {...getFieldProps(`${config}.moldNumber`)}
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack gap={gap}>
        <Stack gap="25px">
          <Stack direction="row" gap={columnGap}>
            <HubHookFormInput
              name={`${config}.waxInjectionTemp`}
              label="Wax Temp"
              placeholder="Ex. 12"
              endAdornment="°F"
              isNumber
              {...getFieldProps(`${config}.waxInjectionTemp`)}
            />
            <HubHookFormInput
              name={`${config}.topPlattenTemp`}
              label="Top Platten Temp"
              placeholder="Ex. 12"
              endAdornment="°F"
              isNumber
              {...getFieldProps(`${config}.topPlattenTemp`)}
            />
            <HubHookFormInput
              name={`${config}.bottomPlattenTemp`}
              label="Bottom Platten Temp"
              placeholder="Ex. 12"
              endAdornment="°F"
              isNumber
              {...getFieldProps(`${config}.bottomPlattenTemp`)}
            />
          </Stack>
          <Stack direction="row" gap={columnGap}>
            <HubHookFormInput
              name={`${config}.waxInjectionPressure`}
              label="Injection Pressure"
              placeholder="Ex. 12"
              endAdornment="psi"
              isNumber
              {...getFieldProps(`${config}.waxInjectionPressure`)}
            />
            <HubHookFormTimePicker
              name={`${config}.injectionTime`}
              label="Injection Time"
              placeholder="Ex. 12"
              fields={["minutes", "seconds"]}
              {...getFieldProps(`${config}.injectionTime`)}
            />
            <HubHookFormInput
              name={`${config}.flowRate`}
              label="Flow Rate"
              placeholder="Ex. 12.0"
              endAdornment="g/s"
              isDecimal
              {...getFieldProps(`${config}.flowRate`)}
            />
            <HubHookFormTimePicker
              name={`${config}.holdTime`}
              label="Hold Time"
              placeholder="Ex. 12"
              fields={["minutes", "seconds"]}
              {...getFieldProps(`${config}.holdTime`)}
            />
          </Stack>

          <HubHookFormInput
            name={`${basePath}.notes`}
            label="Special Instructions"
            placeholder="Wax notes..."
            rows={5}
            multiline
            {...getFieldProps(`${config}.notes`)}
          />
        </Stack>
      </Stack>
      <Stack gap={gap}>
        <Typography variant="boldCaptionMain">Wax Setup</Typography>
        <Stack gap="25px">
          <Stack direction="row" gap={columnGap}>
            <HubHookFormInput
              name={`${config}.cupSize`}
              label="Cup"
              placeholder="Ex. 12"
              endAdornment="oz"
              isNumber
              {...getFieldProps(`${config}.cupSize`)}
            />
            <HubHookFormSelect
              name={`${config}.cupType`}
              label="Cup Type"
              options={cupTypes}
              useDefault
              {...getFieldProps(`${config}.cupType`)}
            />
            <HubHookFormTimePicker
              name={`${config}.setupTimePerTree`}
              label="Setup time"
              placeholder="Ex. 12"
              {...getFieldProps(`${config}.setupTimePerTree`)}
            />
          </Stack>
        </Stack>
      </Stack>

      {HighlightMenu}
    </StyledHubCardStack>
  );
};

export default PartWaxDetails;
